import React, { useEffect, useMemo } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useSimulate } from '../../services/queries/useSimulate';
import { ApiError } from '../../services/http';

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;
const ba = (d: { before: number | null; after: number | null }) =>
  `${pct(d.before)} → ${pct(d.after)}`;

export function SimulatePanel({
  visible,
  onClose,
  action,
  symbol,
  amountUsd,
}: {
  visible: boolean;
  onClose: () => void;
  action: 'buy' | 'sell';
  symbol: string;
  amountUsd: number;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const sim = useSimulate();

  useEffect(() => {
    if (!visible) return;
    // Clear any previous verdict/error so a re-open never shows a stale
    // result alongside the new spinner.
    sim.reset();
    if (symbol && amountUsd > 0) sim.mutate({ action, symbol, amount_usd: amountUsd });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, action, symbol, amountUsd]);

  const errText =
    sim.error instanceof ApiError && sim.error.response?.status === 429
      ? t('simulate.rateLimited')
      : sim.error
        ? t('simulate.invalid')
        : null;
  const r = sim.data;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <Text style={styles.title}>
            {t('simulate.title')} · {symbol}
          </Text>
          {sim.isPending && <ActivityIndicator color={theme.colors.primary} />}
          {errText && <Text style={styles.error}>{errText}</Text>}
          {!sim.isPending && !errText && r && (
            <>
              <Text
                style={[
                  styles.verdict,
                  { color: r.verdict === 'BLOCKED' ? theme.colors.error : theme.colors.success },
                ]}
              >
                {t(`simulate.verdict_${r.verdict}`)}
              </Text>
              <View style={styles.chips}>
                {r.breaches.map((b) => (
                  <Text key={b} style={styles.chip}>
                    {t(`simulate.breach_${b}`, { defaultValue: b })}
                  </Text>
                ))}
              </View>
              {r.flags.includes('es_unavailable') && (
                <Text style={styles.flag}>{t('simulate.esUnavailable')}</Text>
              )}
              <Row label={t('simulate.es')} value={ba(r.deltas.es_pct)} styles={styles} />
              <Row
                label={t('simulate.themeWeight')}
                value={`${pct(r.deltas.theme_pct.before)} → ${pct(r.deltas.theme_pct.after)} (${pct(r.deltas.theme_pct.limit)})`}
                styles={styles}
              />
              <Row
                label={t('simulate.positionPolicy')}
                value={`${pct(r.deltas.position_policy.resulting)} / ${pct(r.deltas.position_policy.limit)}`}
                styles={styles}
              />
              <Row
                label={t('simulate.effectiveN')}
                value={`${r.deltas.effective_n.before ?? '—'} → ${r.deltas.effective_n.after ?? '—'}`}
                styles={styles}
              />
              {r.suggested_alternative_usd !== null && (
                <Text style={styles.alt}>
                  {t('simulate.suggestedAlt', {
                    amount: `$${r.suggested_alternative_usd.toLocaleString()}`,
                  })}
                </Text>
              )}
            </>
          )}
          <TouchableOpacity style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>×</Text>
          </TouchableOpacity>
          <Text style={styles.disclaimer}>{t('common.advisoryDisclaimer')}</Text>
        </View>
      </View>
    </Modal>
  );
}

function Row({ label, value, styles }: { label: string; value: string; styles: any }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowVal}>{value}</Text>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' },
    sheet: {
      backgroundColor: theme.colors.surface,
      borderTopLeftRadius: theme.radii.xl,
      borderTopRightRadius: theme.radii.xl,
      padding: theme.spacing.xl,
    },
    title: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
    },
    verdict: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.sm,
    },
    chips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.md,
    },
    chip: {
      backgroundColor: theme.colors.warningBackground,
      color: theme.colors.warningText,
      borderRadius: theme.radii.full,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      fontSize: theme.typography.fontSizeXs,
      overflow: 'hidden',
    },
    flag: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      marginBottom: theme.spacing.sm,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.divider,
    },
    rowLabel: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeSm },
    rowVal: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    alt: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizeMd,
      marginTop: theme.spacing.md,
    },
    error: {
      color: theme.colors.error,
      fontSize: theme.typography.fontSizeSm,
      marginVertical: theme.spacing.sm,
    },
    close: { position: 'absolute', top: theme.spacing.md, right: theme.spacing.lg },
    closeText: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSize2Xl },
    disclaimer: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
      marginTop: theme.spacing.lg,
    },
  });
}
